import { createTheme } from '@mui/material/styles';
import { colors as defaultColors, typography, radius } from './tokens';
import { raphaColors } from './rapha-tokens';
import { isRaphaDomain } from '../services/subdomain';

export { typography, radius, shadows, motion, spacing } from './tokens';

// Resolve brand colors once at module load time based on subdomain.
// Every `import { colors }` across the codebase picks up the right palette
// without needing a React context or prop drilling.
const _isRapha = isRaphaDomain();
export const colors = _isRapha ? raphaColors : defaultColors;

function buildTheme(c: typeof defaultColors) {
  return createTheme({
    palette: {
      mode: 'dark',
      background: {
        default: c.bg,
        paper: c.surface,
      },
      text: {
        primary: c.text,
        secondary: c.textMuted,
        disabled: c.textFaint,
      },
      primary: {
        main: c.brand,
        light: c.brandBright,
        dark: c.brandDim,
        contrastText: c.textInverse,
      },
      secondary: {
        main: c.data,
        light: '#4CE5FF',
        dark: c.dataDim,
        contrastText: c.textInverse,
      },
      error: {
        main: c.danger,
      },
      warning: {
        main: c.warning,
      },
      info: {
        main: c.info,
      },
      success: {
        main: c.success,
      },
      divider: c.border,
    },
    typography: {
      fontFamily: typography.fontFamilyUi,
      h1: {
        fontSize: '3.5rem',
        fontWeight: 800,
        letterSpacing: '-0.03em',
        lineHeight: 1.05,
      },
      h2: {
        fontSize: '2.5rem',
        fontWeight: 800,
        letterSpacing: '-0.02em',
        lineHeight: 1.1,
      },
      h3: {
        fontSize: '2rem',
        fontWeight: 700,
        letterSpacing: '-0.02em',
        lineHeight: 1.15,
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 700,
        letterSpacing: '-0.015em',
        lineHeight: 1.2,
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 600,
        letterSpacing: '-0.01em',
        lineHeight: 1.3,
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 600,
        letterSpacing: '-0.005em',
        lineHeight: 1.4,
      },
      subtitle1: {
        fontSize: '1rem',
        fontWeight: 500,
        lineHeight: 1.55,
        color: c.textMuted,
      },
      subtitle2: {
        fontSize: '0.875rem',
        fontWeight: 500,
        lineHeight: 1.55,
        color: c.textMuted,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.55,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.55,
        color: c.textMuted,
      },
      button: {
        fontWeight: 600,
        letterSpacing: '0.01em',
        textTransform: 'none',
      },
      caption: {
        fontSize: '0.75rem',
        letterSpacing: '0.02em',
        color: c.textMuted,
      },
      overline: {
        fontSize: '0.75rem',
        fontWeight: 600,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: c.textMuted,
      },
    },
    shape: {
      borderRadius: radius.md,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: c.bg,
            color: c.text,
            backgroundImage:
              `radial-gradient(circle at 20% -10%, ${c.brand}0F 0%, transparent 45%), radial-gradient(circle at 90% 100%, rgba(0,217,255,0.04) 0%, transparent 50%)`,
            backgroundAttachment: 'fixed',
            minHeight: '100vh',
          },
          '*::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '*::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '*::-webkit-scrollbar-thumb': {
            background: c.borderStrong,
            borderRadius: 4,
          },
          '*::-webkit-scrollbar-thumb:hover': {
            background: c.textFaint,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: c.surface,
            backgroundImage: 'none',
            borderRadius: radius.lg,
            border: `1px solid ${c.border}`,
          },
        },
      },
      MuiCard: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundColor: c.surface,
            backgroundImage: 'none',
            borderRadius: radius.lg,
            border: `1px solid ${c.border}`,
            transition:
              'border-color 220ms cubic-bezier(0.22, 0.61, 0.36, 1), box-shadow 220ms cubic-bezier(0.22, 0.61, 0.36, 1), transform 220ms cubic-bezier(0.22, 0.61, 0.36, 1)',
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            padding: '10px 20px',
            fontWeight: 600,
            fontSize: '0.9375rem',
            transition:
              'all 180ms cubic-bezier(0.22, 0.61, 0.36, 1)',
          },
          containedPrimary: {
            background: c.gradBrand,
            color: c.textInverse,
            boxShadow: `0 4px 16px ${c.brandShadow}`,
            '&:hover': {
              background: c.gradBrand,
              boxShadow: `0 6px 24px ${c.brandShadow}`,
              transform: 'translateY(-1px)',
            },
            '&.Mui-disabled': {
              background: c.surfaceElevated,
              color: c.textFaint,
            },
          },
          outlinedPrimary: {
            borderColor: c.border,
            color: c.text,
            '&:hover': {
              borderColor: c.brand,
              backgroundColor: `${c.brand}0F`,
            },
          },
          text: {
            color: c.textMuted,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              color: c.text,
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            backgroundColor: c.bgSubtle,
            '& fieldset': {
              borderColor: c.border,
            },
            '&:hover fieldset': {
              borderColor: c.borderStrong,
            },
            '&.Mui-focused fieldset': {
              borderColor: c.brand,
              borderWidth: 1,
            },
          },
          input: {
            padding: '14px 16px',
            fontSize: '0.9375rem',
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: c.textMuted,
            '&.Mui-focused': {
              color: c.brand,
            },
          },
        },
      },
      MuiLink: {
        defaultProps: {
          underline: 'none',
        },
        styleOverrides: {
          root: {
            color: c.brand,
            fontWeight: 500,
            transition: 'color 180ms',
            '&:hover': {
              color: c.brandBright,
            },
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: c.border,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: `${c.bg}B8`,
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            borderBottom: `1px solid ${c.border}`,
            backgroundImage: 'none',
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            border: `1px solid ${c.border}`,
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            '&.Mui-checked': {
              color: c.brand,
              '& + .MuiSwitch-track': {
                backgroundColor: c.brand,
                opacity: 0.5,
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: radius.pill,
            fontWeight: 500,
          },
        },
      },
    },
  });
}

export const theme = buildTheme(defaultColors);
export const raphaTheme = buildTheme(raphaColors);
